import React, {useCallback, useEffect, useState} from "react";
import {useArticleApi} from "../../../core/hooks/use-article-api";
import {useLoading} from "../../../core/hooks/use-loading.hook";
import {ArticleSearchResultDto} from "../../../core/models/interfaces/article-search-result-dto.interface";
import ArticleSearchbar from "../article-searchbar";
import ArticleFilter from "../article-filter";
import ArticleSorter from "../article-sorter";
import {State, ViewStates} from "@common/components/view-states";
import {LoadingKeys} from "../../../core/models/enums/loading-keys.enum";
import {ViewState} from "@common/models/enums/viev-state.enum";
import EmptyArticleList from "../empty-article-list";
import ArticleList from "../article-list";
import {useArticleForm} from "../../hooks/use-article-form.hook";
import SelectedRelatedArticleTag from "../ui/selected-related-article-tag";
import {ArticleListRequestParams} from "../../../core/models/interfaces/article-list-request-params.interface";
import {mapArticleDetailsToSearchResult} from "../../helpers/map-article-details-to-search-result";

const ArticleFormRelatedArticles = () => {
    const {error, getSearchArticles, getUniversalArticle} = useArticleApi();
    const {isLoading} = useLoading();
    const [articles, setArticles] = useState<ArticleSearchResultDto[]>([]);
    const [universalArticle, setUniversalArticle] = useState<ArticleSearchResultDto | null>(null);
    const [searchParams, setSearchParams] = useState<ArticleListRequestParams>({
        title: '',
        visibility: 'all',
        sortBy: 'title',
        sortOrder: 'asc'
    });
    const {form, updateStepData, articleId} = useArticleForm();

    const relatedArticles = form.watch('relatedArticles') || [];

    const handleArticleClick = (article: ArticleSearchResultDto) => {
        const isAlreadySelected = relatedArticles.some(related => related.id === article.id);

        if (isAlreadySelected) {
            const updatedArticles = relatedArticles.filter(related => related.id !== article.id);
            form.setValue('relatedArticles', updatedArticles);
        } else {
            if (relatedArticles.length < 5) {
                const newRelatedArticle = {id: article.id, title: article.title};
                const updatedArticles = [...relatedArticles, newRelatedArticle];
                form.setValue('relatedArticles', updatedArticles);
            }
        }

        updateStepData('related' as any, {relatedArticles: form.getValues('relatedArticles')});
    };

    const isArticleSelected = (articleId: string) => {
        return relatedArticles.some(related => related.id === articleId);
    };

    const loadArticles = useCallback(async () => {
        try {
            const hasActiveSearch = Boolean(searchParams.title?.trim())
                || searchParams.visibility !== 'all'
                || searchParams.sortBy !== 'title'
                || searchParams.sortOrder !== 'asc';

            if (hasActiveSearch) {
                const result = await getSearchArticles(searchParams);
                const articlesWithoutSelf = result.filter(el => el.id !== articleId);
                setArticles(articlesWithoutSelf);
                setUniversalArticle(null);
            } else {
                const [result, universal] = await Promise.all([
                    getSearchArticles(searchParams),
                    getUniversalArticle()
                ]);
                const articlesWithoutUniversalAndSelf = result
                    .filter(el => el.id !== articleId)
                    .filter(el => !el.isUniversal);
                setArticles(articlesWithoutUniversalAndSelf);
                if (universal && universal.id !== articleId) {
                    setUniversalArticle(mapArticleDetailsToSearchResult(universal));
                } else {
                    setUniversalArticle(null);
                }
            }
        } catch (error) {
            console.error('Błąd podczas ładowania artykułów:', error);
        }
    }, [searchParams, articleId]);

    useEffect(() => {
        void loadArticles();
    }, [searchParams]);

    return (
        <>
            <div className="flex flex-col gap-6 ">
                <h2 className="font-semibold text-2xl">Powiązane artykuły</h2>
                <span className="font-semibold text-lg">Wybierz powiązane artykuły, które pojawiają się na końcu Twojego tekstu.</span>
            </div>
            <div className="bg-white z-10 sticky top-0 flex flex-col gap-4 py-6 border-b border-outline-mild">
                <div className="flex gap-4 items-center">
                    <ArticleSearchbar
                        onResults={(results) => {
                            setArticles(results.filter(el => el.id !== articleId));
                            setUniversalArticle(null);
                        }}
                        searchParams={searchParams}
                    />
                </div>
                <div className="space-x-2">
                    <div className="inline-block w-fit">
                        <ArticleFilter searchParams={searchParams} onSelect={(value) => setSearchParams(value)}/>
                    </div>
                    <div className="inline-block w-fit">
                        <ArticleSorter searchParams={searchParams} onSelect={(value) => setSearchParams(value)}/>
                    </div>
                </div>
                <div>
                    {relatedArticles.map(({title, id}) => (
                        <SelectedRelatedArticleTag id={id} title={title} key={id}/>
                    ))}
                </div>
            </div>
            <div className="pt-6">
                <ViewStates loading={isLoading(LoadingKeys.USE_ARTICLE_API)} error={error}
                            data={[...articles, universalArticle]}>
                    <State state={ViewState.LOADING}/>
                    <State state={ViewState.EMPTY}>
                        <EmptyArticleList/>
                    </State>
                    <State state={ViewState.FETCHED}>
                        {universalArticle && (
                            <div className="mb-4">
                                <ArticleList
                                    showActionsCard={false}
                                    articles={[universalArticle]}
                                    onClick={(article) => handleArticleClick(article!)}
                                    isArticleSelected={isArticleSelected}
                                />
                            </div>
                        )}
                        <ArticleList
                            showActionsCard={false}
                            articles={articles}
                            onClick={(article) => handleArticleClick(article!)}
                            isArticleSelected={isArticleSelected}
                        />
                    </State>
                    <State state={ViewState.ERROR}/>
                </ViewStates>
            </div>
        </>
    );
}

export default ArticleFormRelatedArticles
